import React, { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { BULK } from "@/constants/testIds";
import { Upload, FolderUp, X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Drag-and-drop bulk uploader with folder structure preservation.
 *
 * - Drop multiple files OR a folder onto the dropzone.
 * - Files retain their relative path (webkitRelativePath); the backend
 *   creates folders to mirror the tree.
 * - Concurrency-limited (3 in-flight) with per-file status indicators.
 */
export default function BulkUpload({ roomId, parentId = null, onDone }) {
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const [queue, setQueue] = useState([]); // [{file, path, status, error}]
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (files) => {
    const items = Array.from(files).map((f) => ({
      file: f,
      // webkitRelativePath set when uploaded via <input webkitdirectory>;
      // for drag-drop we set it from the DataTransferItem entry below.
      path: f.webkitRelativePath || f.name,
      status: "queued",
      error: "",
    }));
    setQueue((q) => [...q, ...items]);
  };

  const handleFileChange = (e) => addFiles(e.target.files);
  const handleFolderChange = (e) => addFiles(e.target.files);

  // Walk a directory entry recursively → File objects with .path set
  const walkEntry = async (entry, basePath = "") =>
    new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => {
          file.__path = basePath + file.name;
          resolve([file]);
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const all = [];
        const readBatch = () => {
          reader.readEntries(async (entries) => {
            if (entries.length === 0) { resolve(all); return; }
            for (const e of entries) {
              const inner = await walkEntry(e, basePath + entry.name + "/");
              all.push(...inner);
            }
            readBatch();
          });
        };
        readBatch();
      } else {
        resolve([]);
      }
    });

  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const items = e.dataTransfer.items;
    const collected = [];
    if (items && items.length && items[0].webkitGetAsEntry) {
      for (const it of Array.from(items)) {
        const entry = it.webkitGetAsEntry?.();
        if (entry) {
          const files = await walkEntry(entry);
          collected.push(...files);
        }
      }
    } else {
      for (const f of Array.from(e.dataTransfer.files)) collected.push(f);
    }
    const queued = collected.map((f) => ({
      file: f,
      path: f.__path || f.webkitRelativePath || f.name,
      status: "queued", error: "",
    }));
    setQueue((q) => [...q, ...queued]);
  };

  const start = async () => {
    if (queue.length === 0 || busy) return;
    setBusy(true);
    const concurrency = 3;
    const idxRef = { current: 0 };
    const runNext = async () => {
      while (idxRef.current < queue.length) {
        const i = idxRef.current++;
        setQueue((q) => q.map((it, j) => j === i ? { ...it, status: "uploading" } : it));
        try {
          const fd = new FormData();
          fd.append("file", queue[i].file, queue[i].file.name);
          fd.append("relative_path", queue[i].path);
          if (parentId) fd.append("parent_id", parentId);
          await api.post(`/rooms/${roomId}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setQueue((q) => q.map((it, j) => j === i ? { ...it, status: "done" } : it));
        } catch (e) {
          setQueue((q) => q.map((it, j) => j === i ? { ...it, status: "error", error: formatError(e) } : it));
        }
      }
    };
    await Promise.all(Array(concurrency).fill(0).map(runNext));
    setBusy(false);
    if (onDone) onDone();
  };

  const clear = () => setQueue([]);

  const done = queue.filter((q) => q.status === "done").length;
  const errors = queue.filter((q) => q.status === "error").length;

  return (
    <div className="border border-border bg-card p-5">
      <div
        data-testid={BULK.dropzone}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-md p-10 text-center transition ${
          dragOver ? "border-primary bg-primary/5" : "border-border"
        }`}
      >
        <div className="flex items-center justify-center gap-3 mb-3">
          <Upload className="w-6 h-6 text-muted-foreground" />
          <span className="label-mono text-muted-foreground">Drop files or a folder here</span>
        </div>
        <div className="text-sm text-muted-foreground mb-5">
          Folder structure is preserved. Max 100MB per file.
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            data-testid="bulk-pick-files-btn"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
          >
            <Upload className="w-4 h-4" /> Choose files
          </button>
          <button
            data-testid="bulk-pick-folder-btn"
            onClick={() => folderRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
          >
            <FolderUp className="w-4 h-4" /> Choose folder
          </button>
        </div>
        <input
          data-testid={BULK.fileInput}
          ref={fileRef} type="file" multiple className="hidden"
          onChange={handleFileChange}
        />
        <input
          data-testid={BULK.folderInput}
          ref={folderRef} type="file" multiple
          {...{ webkitdirectory: "", directory: "" }}
          className="hidden" onChange={handleFolderChange}
        />
      </div>

      {queue.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono text-muted-foreground">
              {queue.length} item{queue.length !== 1 && "s"} · {done} done{errors > 0 && ` · ${errors} failed`}
            </div>
            <div className="flex gap-2">
              <button onClick={clear} disabled={busy}
                className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition disabled:opacity-50">
                Clear
              </button>
              <button
                data-testid={BULK.startBtn}
                onClick={start} disabled={busy || queue.every((q) => q.status !== "queued")}
                className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
              >
                {busy ? "Uploading…" : `Upload ${queue.filter((q) => q.status === "queued").length} files`}
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-auto border border-border rounded-md">
            {queue.map((it, i) => (
              <div key={`${it.path}:${i}`} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-b-0 text-sm">
                {it.status === "queued" && <div className="w-4 h-4 rounded-full border border-border" />}
                {it.status === "uploading" && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                {it.status === "done" && <CheckCircle2 className="w-4 h-4 text-accent" />}
                {it.status === "error" && <AlertCircle className="w-4 h-4 text-destructive" />}
                <span className="flex-1 truncate mono text-xs">{it.path}</span>
                <span className="text-xs text-muted-foreground">
                  {(it.file.size / 1024).toFixed(1)} KB
                </span>
                {it.status === "error" && (
                  <span className="text-xs text-destructive truncate max-w-[200px]" title={it.error}>
                    {it.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
